import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { ChevronDown, ChevronUp, Loader2, Send, UserCheck, UserCircle2, UserPlus, UserX } from "lucide-react";
import { toast } from "sonner";

const PERM_LABELS = {
  "orders.update_status": "Change workflow status (printing → ready)",
  "orders.update_payment": "Change payment status (paid / unpaid / cancelled)",
  "orders.claim": "Claim & fulfill orders",
  "orders.reassign": "Reassign orders to other staff",
  "orders.dispatch": "Dispatch — hand off ready orders & mark completed",
  "orders.view_pii": "View customers' contact details (phone) & export orders to CSV",
  "pricing.edit": "Edit pricing",
  "stats.view": "View revenue & stats",
  "notifications.send_test": "Send test notifications",
  "staff.manage": "Manage staff accounts",
  "audit.view": "View audit trail",
};

export default function StaffTab() {
  const [staff, setStaff] = useState([]);
  const [permKeys, setPermKeys] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [presets, setPresets] = useState({});
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", permissions: [] });
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    setLoading(true);
    const [s, p] = await Promise.all([
      api.get("/admin/staff"),
      api.get("/admin/staff/permissions"),
    ]);
    setStaff(s.data);
    setPermKeys(p.data.keys);
    setDefaults(p.data.defaults);
    setPresets(p.data.presets || {});
    if (!form.permissions.length) setForm((f) => ({ ...f, permissions: p.data.defaults }));
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const togglePerm = (perm) => {
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(perm)
        ? f.permissions.filter((p) => p !== perm)
        : [...f.permissions, perm],
    }));
  };

  const createStaff = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.password) {
      toast.error("Name, email, password are required");
      return;
    }
    setCreating(true);
    try {
      await api.post("/admin/staff", form);
      toast.success(`Created staff: ${form.name}`);
      setForm({ name: "", email: "", phone: "", password: "", permissions: defaults });
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Create failed");
    }
    setCreating(false);
  };

  const updatePerms = async (staffId, perms) => {
    try {
      await api.patch(`/admin/staff/${staffId}`, { permissions: perms });
      toast.success("Permissions updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Update failed");
    }
  };

  const setActive = async (staffId, active) => {
    try {
      await api.patch(`/admin/staff/${staffId}`, { active });
      toast.success(active ? "Staff reactivated" : "Staff deactivated");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Update failed");
    }
  };

  const resetPassword = async (staffId) => {
    const np = window.prompt("New password (min 6 chars):");
    if (!np || np.length < 6) return;
    try {
      await api.patch(`/admin/staff/${staffId}`, { password: np });
      toast.success("Password reset");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Update failed");
    }
  };

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-[#059669]" />;

  const staffOnly = staff.filter((u) => u.role === "staff");

  return (
    <div className="space-y-6" data-testid="staff-tab">
      {/* Create form */}
      <section className="bg-white border border-[#0A0A0A] p-6">
        <div className="flex items-center gap-2 mb-4">
          <UserPlus className="w-5 h-5 text-[#059669]" />
          <div className="font-display font-bold text-xl">Add new staff member</div>
        </div>
        <form onSubmit={createStaff} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <input
              data-testid="new-staff-name"
              placeholder="Full name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none"
            />
            <input
              data-testid="new-staff-email"
              placeholder="staff@ctpkenya.com"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none mono"
            />
            <input
              data-testid="new-staff-phone"
              placeholder="07XXXXXXXX"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none mono"
            />
            <input
              data-testid="new-staff-password"
              placeholder="Temporary password"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none mono"
            />
          </div>
          <div>
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252]">Permissions</div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#525252]">Quick preset:</span>
                <select
                  value=""
                  onChange={(e) => {
                    const k = e.target.value;
                    if (k && presets[k]) setForm({ ...form, permissions: [...presets[k]] });
                  }}
                  data-testid="new-staff-preset"
                  className="px-2 py-1 border border-[#0A0A0A] text-[10px] uppercase tracking-[0.15em] font-bold bg-white"
                >
                  <option value="">— choose —</option>
                  {Object.keys(presets).map((k) => (
                    <option key={k} value={k}>{k.replace("_", " ")} ({presets[k].length})</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, permissions: [] })}
                  className="text-[10px] uppercase tracking-[0.15em] font-bold px-2 py-1 border border-[#E5E5E5] hover:border-[#DC2626] hover:text-[#DC2626]"
                >
                  Clear
                </button>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {permKeys.map((p) => (
                <label key={p} className="flex items-start gap-2 cursor-pointer border border-[#E5E5E5] p-2 hover:border-[#0A0A0A]">
                  <input
                    type="checkbox"
                    checked={form.permissions.includes(p)}
                    onChange={() => togglePerm(p)}
                    data-testid={`new-staff-perm-${p}`}
                    className="mt-1 accent-[#059669]"
                  />
                  <div>
                    <div className="text-xs font-bold mono">{p}</div>
                    <div className="text-[10px] text-[#525252]">{PERM_LABELS[p] || ""}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <button
            type="submit"
            disabled={creating}
            data-testid="create-staff-btn"
            className="inline-flex items-center gap-2 bg-[#059669] text-white px-6 py-3 text-sm uppercase tracking-[0.2em] font-bold hover:bg-[#047857] disabled:opacity-50"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Create staff
          </button>
        </form>
      </section>

      {/* Staff list */}
      <section>
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-3">§ Existing staff ({staffOnly.length})</div>
        <div className="bg-white border border-[#E5E5E5] divide-y divide-[#E5E5E5]" data-testid="staff-list">
          {staffOnly.length === 0 && (
            <div className="p-6 text-center text-[#525252]">No staff yet. Create one above.</div>
          )}
          {staffOnly.map((s) => (
            <div key={s.id} className={`p-4 ${s.active === false ? "opacity-50" : ""}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 bg-[#0A0A0A] text-white flex items-center justify-center">
                    {s.active === false ? <UserX className="w-5 h-5" /> : <UserCircle2 className="w-5 h-5" />}
                  </div>
                  <div className="min-w-0">
                    <div className="font-bold truncate">{s.name}</div>
                    <div className="text-xs text-[#525252] mono truncate">{s.email}{s.phone ? ` · ${s.phone}` : ""}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 border border-[#0A0A0A] text-[10px] uppercase tracking-[0.15em] font-bold">
                    {(s.permissions || []).length} perms
                  </span>
                  {s.active === false ? (
                    <button onClick={() => setActive(s.id, true)} data-testid={`activate-${s.id}`} className="text-[10px] uppercase tracking-[0.15em] font-bold px-3 py-1.5 border-2 border-[#059669] text-[#059669] hover:bg-[#059669] hover:text-white inline-flex items-center gap-1">
                      <UserCheck className="w-3 h-3" /> Reactivate
                    </button>
                  ) : (
                    <button onClick={() => setActive(s.id, false)} data-testid={`deactivate-${s.id}`} className="text-[10px] uppercase tracking-[0.15em] font-bold px-3 py-1.5 border border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white inline-flex items-center gap-1">
                      <UserX className="w-3 h-3" /> Deactivate
                    </button>
                  )}
                  <button onClick={() => resetPassword(s.id)} data-testid={`reset-pw-${s.id}`} className="text-[10px] uppercase tracking-[0.15em] font-bold px-3 py-1.5 border border-[#E5E5E5] hover:border-[#0A0A0A]">
                    Reset password
                  </button>
                  <button onClick={() => setOpenId(openId === s.id ? null : s.id)} className="w-8 h-8 border border-[#E5E5E5] hover:border-[#0A0A0A] flex items-center justify-center">
                    {openId === s.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {openId === s.id && (
                <div className="mt-4 border-t border-[#E5E5E5] pt-4">
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252]">Permissions</div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] uppercase tracking-[0.2em] text-[#525252]">Apply preset:</span>
                      <select
                        value=""
                        onChange={(e) => {
                          const k = e.target.value;
                          if (k && presets[k]) updatePerms(s.id, [...presets[k]]);
                        }}
                        data-testid={`preset-${s.id}`}
                        className="px-2 py-1 border border-[#0A0A0A] text-[10px] uppercase tracking-[0.15em] font-bold bg-white"
                      >
                        <option value="">— choose —</option>
                        {Object.keys(presets).map((k) => (
                          <option key={k} value={k}>{k.replace("_", " ")} ({presets[k].length})</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2 mb-3">
                    {permKeys.map((p) => {
                      const checked = (s.permissions || []).includes(p);
                      return (
                        <label key={p} className="flex items-start gap-2 cursor-pointer border border-[#E5E5E5] p-2 hover:border-[#0A0A0A]">
                          <input
                            type="checkbox"
                            checked={checked}
                            data-testid={`perm-${s.id}-${p}`}
                            onChange={() => {
                              const np = checked ? (s.permissions || []).filter((x) => x !== p) : [...(s.permissions || []), p];
                              updatePerms(s.id, np);
                            }}
                            className="mt-1 accent-[#059669]"
                          />
                          <div>
                            <div className="text-xs font-bold mono">{p}</div>
                            <div className="text-[10px] text-[#525252]">{PERM_LABELS[p] || ""}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
